'use client'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { SearchResultItem } from '@/lib/types'
import Link from 'next/link'
import { useState } from 'react'

export interface SearchResultsProps {
  results: SearchResultItem[]
  displayMode?: 'grid' | 'list'
}

export function SearchResults({
  results,
  displayMode = 'grid'
}: SearchResultsProps) {
  // State to manage whether to display the results
  const [showAllResults, setShowAllResults] = useState(false)

  const handleViewMore = () => {
    setShowAllResults(true)
  }

  const isValidUrl = (url: string) => {
    try {
      // eslint-disable-next-line no-new
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  const safeResults = Array.isArray(results)
    ? results.filter(r => typeof r?.url === 'string' && isValidUrl(r.url))
    : []

  // Logic for grid mode
  const displayedGridResults = showAllResults
    ? safeResults
    : safeResults.slice(0, 3)
  const additionalResultsCount = safeResults.length > 3 ? safeResults.length - 3 : 0
  const displayUrlName = (url: string) => {
    const hostname = new URL(url).hostname
    const parts = hostname.split('.')
    return parts.length > 2 ? parts.slice(1, -1).join('.') : parts[0]
  }

  // --- List Mode Rendering ---
  if (displayMode === 'list') {
    return (
      <div className="flex flex-col gap-2">
        {safeResults.map((result, index) => {
          const urlObj = new URL(result.url)
          const hostname = urlObj.hostname
          const pathname = urlObj.pathname
          return (
            <Link
              href={result.url}
              key={index}
              passHref
              target="_blank"
              className="block"
            >
              <Card className="w-full hover:bg-muted/50 transition-colors">
                <CardContent className="p-2 flex items-start space-x-2">
                  <Avatar className="h-4 w-4 mt-1 flex-shrink-0">
                    <AvatarImage
                      src={`https://www.google.com/s2/favicons?domain=${hostname}`}
                      alt={hostname}
                    />
                    <AvatarFallback className="text-xs">
                      {hostname[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-grow overflow-hidden space-y-0.5">
                    <p className="text-sm font-medium line-clamp-1">
                      {result.title || pathname}
                    </p>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {result.content}
                    </p>
                    <div className="text-xs text-muted-foreground/80 mt-1 truncate">
                      <span className="underline">{hostname}</span>{' '}
                      - {index + 1}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          )
        })}
      </div>
    )
  }

  // --- Grid Mode Rendering (Existing Logic) ---
  return (
    <div className="flex flex-wrap -m-1">
      {displayedGridResults.map((result, index) => {
        const urlObj = new URL(result.url)
        const hostname = urlObj.hostname
        return (
          <div className="w-1/2 md:w-1/4 p-1" key={index}>
            <Link href={result.url} passHref target="_blank">
              <Card className="flex-1 h-full hover:bg-muted/50 transition-colors">
                <CardContent className="p-2 flex flex-col justify-between h-full">
                  <p className="text-xs line-clamp-2 min-h-[2rem]">
                    {result.title || result.content}
                  </p>
                  <div className="mt-2 flex items-center space-x-1">
                    <Avatar className="h-4 w-4">
                      <AvatarImage
                        src={`https://www.google.com/s2/favicons?domain=${hostname}`}
                        alt={hostname}
                      />
                      <AvatarFallback>
                        {hostname[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="text-xs opacity-60 truncate">
                      {`${displayUrlName(result.url)} - ${index + 1}`}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </div>
        )
      })}
      {!showAllResults && additionalResultsCount > 0 && (
        <div className="w-1/2 md:w-1/4 p-1">
          <Card className="flex-1 flex h-full items-center justify-center">
            <CardContent className="p-2">
              <Button
                variant={'link'}
                className="text-muted-foreground"
                onClick={handleViewMore}
              >
                View {additionalResultsCount} more
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
